import { Group, Modal, Stack, Title } from '@mantine/core';
import { useForm } from '@mantine/form';

import { useTranslation } from 'react-i18next';

import { Button } from '../../../shared/components/UI/Button/Button';
import { TextInput } from '../../../shared/components/UI/TextInput/TextInput';

import type { CreateWorkspaceUserDTO } from '../../../shared/api/magellan/workspaces/dto';
import { Role } from '../../../shared/entities/Access';
import { isValidEmail } from '../../../shared/utils/strings';
import { useCreateWorkspaceUser } from '../hooks/useCreateWorkspaceUser.hook';
import { RoleSelect } from './RoleSelect';

export function CreateWorkspaceUserModal({
  opened,
  onClose,
}: {
  opened: boolean;
  onClose: () => void;
}) {
  const { t } = useTranslation(['workspaceSettings', 'common']);
  const { mutate: createWorkspaceUser } = useCreateWorkspaceUser();

  const form = useForm<CreateWorkspaceUserDTO>({
    initialValues: {
      email: '',
      firstName: '',
      lastName: '',
      role: Role.CONTRIBUTOR,
    },
    validateInputOnBlur: true,
    validate: {
      email: (value: string) => {
        if (!isValidEmail(value)) {
          return t('inputs.email.error');
        }
        return null;
      },
      firstName: (value: string) =>
        value.length > 0 ? null : t('inputs.firstName.error'),
      lastName: (value: string) =>
        value.length > 0 ? null : t('inputs.lastName.error'),
    },
  });

  const handleClose = () => {
    form.reset();
    onClose();
  };

  const handleSubmit = (values: CreateWorkspaceUserDTO) => {
    if (!form.isValid()) return;

    createWorkspaceUser(values);
    handleClose();
  };

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      withCloseButton={false}
      radius="12px"
      styles={{ content: { flex: 'initial !important', width: '680px' } }}
    >
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Stack spacing="05" p="05">
          <Title order={4}>{t('addWorkspaceUser')}</Title>

          <TextInput
            label={t('inputs.email.label')}
            required
            placeholder={t('inputs.email.placeholder')}
            {...form.getInputProps('email')}
          />

          <TextInput
            label={t('inputs.lastName.label')}
            required
            placeholder={t('inputs.lastName.placeholder')}
            {...form.getInputProps('lastName')}
          />

          <TextInput
            label={t('inputs.firstName.label')}
            required
            placeholder={t('inputs.firstName.placeholder')}
            {...form.getInputProps('firstName')}
          />

          <RoleSelect required {...form.getInputProps('role')} />

          <Group noWrap spacing="02" position="right">
            <Button variant="subtle" onClick={handleClose}>
              {t('common:cancel')}
            </Button>
            <Button type="submit">{t('common:validate')}</Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}
